import { TaskSubTicketResource } from 'dy-frontend-http-repository/lib/modules/TaskSubTicket/resources';
import { ImageHashPreviewSize } from 'dy-frontend-shared/lib/data/valueObjects/ImageHashPreview/enums';
import moment from 'moment';
import React, { HTMLAttributes } from 'react';

import Avatar from '@app/components/Avatar';
import RouterLink from '@app/components/RouterLink';
import DevText from '@app/components/Text';
import { Endpoints, imageHashPreview, taskSubTicketStatusInformation } from '@app/data/consts';
import { Button, Card, Colors, Divider, Intent, Tag } from '@blueprintjs/core';
import Flex from '@components/Flex';
import Heading from '@components/Heading';

import { formatLoggedMinutes, formatSubTicketTitle, getSubTicketStatusTagStyle, isSubTicketOverdue } from '../../utils';

export interface SubTicketListItemProps {
    subTicket: TaskSubTicketResource;
    nowMs: number;
    isExpanded: boolean;
    onToggleExpanded: () => void;

    // The accessible drag handle, rendered at the head of the row. A slot so the row stays
    // presentational and the page keeps sole ownership of what the order actually is
    ordering?: React.ReactNode;

    // Read-only detail panel, rendered only while expanded
    detail?: React.ReactNode;
    // Rendered under the summary: the designer workspace for the assignee, management controls
    // for everyone else. Kept as a slot so the row itself stays presentational
    actions?: React.ReactNode;
    workspace?: React.ReactNode;
}

export type Props = HTMLAttributes<HTMLDivElement> & SubTicketListItemProps;

const SubTicketListItem: React.FC<Props> = ({
    subTicket,
    nowMs,
    isExpanded,
    onToggleExpanded,
    ordering,
    detail,
    actions,
    workspace,
    ...props
}) => {
    const assignee = subTicket.assignee_user;
    const statusInformation = taskSubTicketStatusInformation[subTicket.status];
    const isArchived = subTicket.archived_at !== null;
    const isOverdue = isSubTicketOverdue(subTicket.due_at, subTicket.status, nowMs);

    // The scope summary is what a designer recognises the phase by, so it IS the card title.
    // It is server-derived from the scope on every write, so it is always in step with it and
    // there is nothing to recompute here. The `#ordinal` label remains the fallback for a phase
    // with no summary at all — it says which phase, never which work.
    const title = formatSubTicketTitle({
        scopeSummary: subTicket.scope_summary,
        fallback: `Sub-task #${subTicket.ordinal}`,
    });

    let avatarSrc: string | null = null;
    if (assignee.image_hash) {
        avatarSrc = imageHashPreview.userImage(assignee.image_hash, ImageHashPreviewSize.XS);
    }

    const renderHeader = () => (
        <Flex direction="row" align="center" justify="space-between">
            <Flex direction="row" align="center" style={{ minWidth: 0, flexWrap: 'wrap' }}>
                {ordering}

                <Button
                    className="mr-1"
                    minimal
                    small
                    icon={isExpanded ? 'chevron-down' : 'chevron-right'}
                    onClick={onToggleExpanded}
                    aria-label={isExpanded ? 'Collapse sub-task detail' : 'Expand sub-task detail'}
                />

                {/* Truncated for the eye only: the whole summary stays reachable to a pointer
                    through `title` and to a screen reader through `aria-label` */}
                <Heading className="mr-1" type="h4" title={title.full} aria-label={title.full}>
                    {title.display}
                </Heading>

                {/* Where the phase actually is, said on the collapsed card: reading a board
                    should not cost one expand per row. Overdue and Archived stay behind the
                    expand — they are exceptions to raise, not the identity of the row. */}
                <Tag
                    className="mr-1"
                    style={getSubTicketStatusTagStyle(subTicket.status)}
                    aria-label={`Phase status: ${statusInformation.title}`}
                >
                    {statusInformation.title}
                </Tag>

                {isExpanded && (
                    <>
                        {isOverdue && (
                            <Tag className="mr-1" minimal intent={Intent.DANGER}>
                                Overdue
                            </Tag>
                        )}

                        {isArchived && (
                            <Tag className="mr-1" minimal>
                                Archived
                            </Tag>
                        )}
                    </>
                )}
            </Flex>

            {isExpanded && actions}
        </Flex>
    );

    // Neither the scope summary nor the expected deliverables are repeated in the body: the
    // summary is the title above, and the deliverables belong to the expanded detail. A card
    // that says the same thing twice is a card nobody finishes reading.
    const renderMeta = () => (
        <Flex className="mt-2" direction="row" align="center" justify="space-between">
            <Flex direction="row" align="center">
                <Avatar
                    className="mr-1"
                    width="28px"
                    height="28px"
                    alt={`${assignee.first_name} ${assignee.last_name}`}
                    src={avatarSrc}
                />

                <DevText>
                    <RouterLink color={Colors.WHITE} to={Endpoints.CORE_USER_TASKS.replace(':coreUserId', assignee.id)}>
                        {assignee.first_name} {assignee.last_name}
                    </RouterLink>
                </DevText>
            </Flex>

            <Flex direction="row" align="center">
                {/* A phase may be planned without a due date. It says so plainly rather than
                    formatting a null into "Invalid date", and there is no "Due" label in front
                    of a date that does not exist */}
                {subTicket.due_at === null ? (
                    <DevText muted className="mr-2">
                        No due date
                    </DevText>
                ) : (
                    <>
                        <DevText muted className="mr-1">
                            Due
                        </DevText>
                        <DevText className="mr-2" color={isOverdue ? Colors.RED4 : undefined}>
                            {moment(subTicket.due_at).format('D MMM YYYY')}
                        </DevText>
                    </>
                )}

                <DevText muted className="mr-1">
                    Logged
                </DevText>
                <DevText>{formatLoggedMinutes(subTicket.total_logged_min)}</DevText>
            </Flex>
        </Flex>
    );

    return (
        <Card {...props}>
            {renderHeader()}
            {renderMeta()}

            {isExpanded && detail}

            {isExpanded && workspace && (
                <>
                    <Divider className="mt-2 mb-2" />
                    {workspace}
                </>
            )}
        </Card>
    );
};

export default SubTicketListItem;
